import { useState } from "react";
import { ArrowRight, Check, Loader2, ShieldCheck } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";
import { Hint, SideCard, Steps, type PanelProps } from "./channel-wizard-kit";

/* Instagram через Facebook: вход в Meta, выбор бизнес-страницы
   и подтверждение доступа к сообщениям. */

type Page = {
  id: string;
  name: string;
  handle: string;
  followers: string;
  linked: boolean;
};

const PAGES: Page[] = [
  { id: "hub-market", name: "Hub Market", handle: "hub.market", followers: t("24,8 тыс. подписчиков"), linked: true },
  { id: "hub-kids", name: "Hub Kids", handle: "hub.kids", followers: t("8,1 тыс. подписчиков"), linked: true },
  { id: "hub-kz", name: "Hub Store KZ", handle: "hub.store.kz", followers: t("Instagram не привязан"), linked: false },
];

export default function InstagramFacebookPanel({ onDone }: PanelProps) {
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pageId, setPageId] = useState<string | null>(null);

  const page = PAGES.find((p) => p.id === pageId && p.linked) ?? null;

  const signIn = () => {
    setBusy(true);
    setTimeout(() => {
      setBusy(false);
      setSigned(true);
    }, 900);
  };

  const connect = () => {
    if (!page) return;
    onDone(`${page.name} · @${page.handle}`, [
      { label: t("Тип"), value: t("Instagram · через Facebook") },
      { label: t("Страница"), value: page.name },
      { label: "Instagram", value: `@${page.handle}` },
      { label: t("Доступ"), value: t("сообщения и сторис") },
    ]);
  };

  return (
    <div className="grid flex-1 overflow-y-auto lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0 p-4 md:p-5">
        <Steps
          active={page ? 3 : signed ? 2 : 1}
          items={[
            t("Войдите в Facebook под администратором страницы"),
            t("Выберите бизнес-страницу с привязанным Instagram"),
            t("Разрешите доступ к сообщениям — и канал подключён"),
          ]}
        />

        {!signed ? (
          <div className="mt-4">
            <div className="rounded-btn border border-border bg-background p-3">
              <p className="text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Откроется окно Meta. Hub запросит доступ только к сообщениям страницы и имени профиля — публиковать ничего не сможем.")}
              </p>
            </div>
            <button
              type="button"
              onClick={signIn}
              disabled={busy}
              className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : <i className="ri-facebook-circle-line text-base leading-none" aria-hidden />}
              {busy ? t("Открываем Meta…") : t("Войти через Facebook")}
            </button>
          </div>
        ) : (
          <div className="mt-4">
            <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-emerald-600">
              <Check className="size-3.5" strokeWidth={3} aria-hidden />
              {t("Вход выполнен: admin@hub.market")}
            </p>

            <p className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("Бизнес-страница")}
            </p>
            <div role="radiogroup" aria-label={t("Бизнес-страница")} className="mt-1.5 flex flex-col gap-2">
              {PAGES.map((p) => {
                const active = pageId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={!p.linked}
                    onClick={() => setPageId(p.id)}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2.5 rounded-btn border px-2.5 py-2 text-start transition",
                      active
                        ? "border-primary/50 bg-primary/5"
                        : "border-border bg-background hover:border-primary/40",
                      !p.linked && "cursor-not-allowed opacity-50 hover:border-border",
                    )}
                  >
                    <span
                      className={cn(
                        "grid size-8 flex-none place-items-center rounded-full text-sm leading-none",
                        active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary",
                      )}
                      aria-hidden
                    >
                      <i className="ri-instagram-line" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[12px] font-semibold leading-snug">{p.name}</span>
                      <span className="block truncate text-[10.5px] text-muted-foreground">{p.followers}</span>
                    </span>
                    {p.linked ? (
                      <span className="flex-none text-[10.5px] font-medium text-muted-foreground">@{p.handle}</span>
                    ) : (
                      <span className="flex-none rounded-full bg-muted px-1.5 py-0.5 text-[9.5px] font-semibold text-muted-foreground">
                        {t("недоступно")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={connect}
              disabled={!page}
              className="mt-3 inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("Подключить страницу")}
              <ArrowRight className="size-3.5" aria-hidden />
            </button>
          </div>
        )}

        <Hint icon={<ShieldCheck className="size-3.5" />}>
          {t("Доступ можно отозвать в любой момент в настройках Meta Business Suite.")}
        </Hint>
      </div>

      <div className="flex min-w-0 flex-col gap-3 border-t border-border bg-background p-4 lg:border-s lg:border-t-0 md:p-5">
        <SideCard title={t("Выбранная страница")}>
          {page ? (
            <div className="mt-2.5">
              <p className="text-[12.5px] font-semibold">{page.name}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">Instagram · @{page.handle}</p>
              <div className="mt-2 flex items-center gap-1.5 rounded-btn bg-muted/70 px-2 py-1.5">
                <Check className="size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                <span className="text-[11px] text-foreground">{t("сообщения и сторис разрешены")}</span>
              </div>
            </div>
          ) : (
            <p className="mt-2 text-[11.5px] leading-relaxed text-muted-foreground">
              {t("Сначала войдите через Facebook и выберите страницу — здесь появятся её данные.")}
            </p>
          )}
        </SideCard>

        <SideCard title={t("Что будет после подключения")}>
          <ul className="mt-2 flex flex-col gap-1.5">
            {[
              t("Instagram Direct и Messenger в одной ленте"),
              t("Ответы на сторис и комментарии"),
              t("Права берутся из Meta Business Suite"),
            ].map((item) => (
              <li key={item} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Check className="mt-0.5 size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                {item}
              </li>
            ))}
          </ul>
        </SideCard>
      </div>
    </div>
  );
}